/**
 * PruefberichtContainer - the Prüfbericht of a Vorgang, loaded and decided (P4 step 19, P5 steps
 * 17-21). Mounted on the task detail of a mandate review task and on the customer page's Vorgang row
 * of a returned mandate, so the review the controls open can actually be decided in the product.
 *
 * Reads GET /vorgaenge/:id/pruefbericht; renders nothing while loading, when the Vorgang has no
 * control run (404) or, with `onlyInReview`, when the latest run is not an undecided review. The
 * decision posts through the api (which re-checks everything) and refreshes the report, the task
 * lists and the customer's Vorgänge and Mandatskacheln.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { PruefberichtEntscheidBody } from '@brokly/shared';
import { apiClient, ApiError } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { useEffectivePermissions } from '../../administration/useEffectivePermissions';
import { PruefberichtPanel } from './PruefberichtPanel';

/** The decision's refusals in the broker's words (the codes stay in the api). */
const DECIDE_ERROR_MESSAGE: Record<string, string> = {
  scope_confirmation_required: 'Bitte bestätigen Sie den Mandatsumfang.',
  scope_addition_unknown: 'Eine übernommene Versicherung gehört nicht zu diesem Rücklauf. Bitte laden Sie den Bericht neu.',
  scope_not_applicable: 'Für diesen Bericht gibt es keinen Mandatsumfang zu bestätigen.',
  confirmation_required: 'Bitte bestätigen Sie alle markierten Punkte.',
  pruefbericht_run_stale: 'Inzwischen ist ein neuerer Rücklauf eingegangen. Der aktuelle Bericht wird geladen.',
  pruefbericht_already_decided: 'Dieser Bericht wurde bereits entschieden.',
  pruefbericht_not_in_review: 'Dieser Bericht braucht keinen Entscheid.',
  vorgang_closed: 'Der Vorgang ist bereits abgeschlossen oder abgebrochen.',
  vollmacht_document_invalid: 'Das gewählte Vollmacht-Dokument gehört nicht zu diesem Mandat.',
  mandate_not_sent: 'Das Mandat ist nicht mehr im Versand.',
  blacklist_restricted: 'Sie dürfen diesen Kunden nicht bearbeiten.',
};

function decideErrorMessage(code: string | undefined): string {
  return (code && DECIDE_ERROR_MESSAGE[code]) ?? 'Der Entscheid war nicht möglich. Bitte versuchen Sie es erneut.';
}

export function PruefberichtContainer({ vorgangId, onlyInReview = false }: { vorgangId: string; onlyInReview?: boolean }) {
  const qc = useQueryClient();
  const { can } = useEffectivePermissions();
  const [error, setError] = useState<string | null>(null);
  const report = useQuery({
    queryKey: qk.pruefbericht(vorgangId),
    queryFn: () => apiClient.vorgaenge.pruefbericht(vorgangId),
    retry: false,
  });

  const decide = useMutation({
    mutationFn: (body: PruefberichtEntscheidBody) => apiClient.vorgaenge.entscheid(vorgangId, body),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.pruefbericht(vorgangId) });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['vorgaenge', 'board'] });
      void qc.invalidateQueries({ queryKey: ['mandate'] });
      void qc.invalidateQueries({ queryKey: ['customer-status'] });
    },
    onError: (e: unknown) => {
      setError(decideErrorMessage(e instanceof ApiError ? e.code : undefined));
      // A newer run or a decision taken elsewhere: show the current report.
      void qc.invalidateQueries({ queryKey: qk.pruefbericht(vorgangId) });
    },
  });

  const data = report.data;
  if (!data) return null;
  if (onlyInReview && !(data.run.overall === 'review' && data.run.decision === null)) return null;

  return (
    <div className="flex flex-col gap-2">
      <PruefberichtPanel
        key={data.run.id}
        report={data}
        can={can}
        loadSignatureImage={(personId, which) => apiClient.vorgaenge.signatureImage(vorgangId, personId, which)}
        onDecide={(body) => decide.mutate(body)}
      />
      {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
